// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API module for retrieving customer reports from the Hardfin system.
 * Provides type-safe access to customer asset status and ownership reports.
 */
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { sparseCustomerSchema } from '@/sparse/api/types';
import { z } from 'zod';

/**
 * Schema for customer asset functional status report.
 * Tracks the count of functional and non-functional assets for each customer.
 * Automatically calculates the total count of assets.
 */
const customerFunctionalStatusReport = z
  .object({
    customer: sparseCustomerSchema,
    functionalCount: z.number(),
    needsReviewCount: z.number(),
    nonFunctionalCount: z.number(),
  })
  .transform((report) => ({
    ...report,
    totalCount:
      report.functionalCount +
      report.needsReviewCount +
      report.nonFunctionalCount,
  }));

/**
 * Type definition for customer functional status report.
 * Includes customer details and counts of functional/non-functional assets.
 */
export type CustomerFunctionalStatusReport = z.infer<
  typeof customerFunctionalStatusReport
>;

/**
 * Schema for customer asset ownership report.
 * Tracks the count of owned and sold assets for each customer.
 * Automatically calculates the total count of assets.
 */
const customerOwnershipReport = z
  .object({
    customer: sparseCustomerSchema,
    ownedCount: z.number(),
    soldCount: z.number(),
  })
  .transform((report) => ({
    ...report,
    totalCount: report.ownedCount + report.soldCount,
  }));

/**
 * Type definition for customer ownership report.
 * Includes customer details and counts of owned/sold assets.
 */
export type CustomerOwnershipReport = z.infer<typeof customerOwnershipReport>;

/**
 * Schema for the complete customer reports API response.
 * Contains both functional status and ownership reports for all customers.
 */
const responseSchema = z
  .object({
    data: z.object({
      functional: z.array(customerFunctionalStatusReport),
      ownership: z.array(customerOwnershipReport),
    }),
  })
  .transform(({ data }) => data);

/**
 * Retrieves comprehensive customer reports from the Hardfin system.
 * Includes both functional status reports (tracking asset functionality)
 * and ownership reports (tracking asset ownership status) for all customers.
 *
 * @returns Promise resolving to an object containing both functional status and ownership reports
 * @throws Will throw an error if the API request fails or response validation fails
 */
export async function getCustomerReport() {
  const path = '/v1/customer-reports';
  const response = await api.get(path);

  return parseResponse(responseSchema, response.data, {
    schemaName: 'CustomerReport',
    endpoint: path,
  });
}
